import { useState, useCallback, useEffect } from 'react';

const TOTAL_INVADERS = 5;

const playBlip = (hitCount: number) => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const t = ctx.currentTime;

    const tone = (freq: number, start: number, dur: number, type: OscillatorType, vol = 0.2) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, start);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.15, start + dur);
      gain.gain.setValueAtTime(vol, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };

    // Laser shot: sharp fast downward sweep
    const base = Math.min(900 + hitCount * 60, 2200);
    tone(base, t, 0.08, 'sawtooth', 0.22);
    // Death zap: lower punchy thud right behind it
    tone(base * 0.35, t + 0.03, 0.14, 'square', 0.15);
    // Noise burst: short white-noise "splat"
    const bufLen = ctx.sampleRate * 0.06;
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    const noiseGain = ctx.createGain();
    noise.buffer = buf;
    noise.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noiseGain.gain.setValueAtTime(0.12, t + 0.02);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    noise.start(t + 0.02);
    noise.stop(t + 0.08);
  } catch (_) {
    // Audio not available — silent fail
  }
};

const KILL_SOUND_DURATION_MS = 220;
const COIN_SPACING = 0.42; // seconds between each coin

const playCoin = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

    const coin = (startOffset: number) => {
      const t = ctx.currentTime + startOffset;
      const note = (freq: number, start: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.25, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
        osc.start(start);
        osc.stop(start + dur);
      };
      note(988, t, 0.08);        // B5 — pickup
      note(1319, t + 0.09, 0.3); // E6 — ring
    };

    // Three coins, spaced out, after the kill sound finishes
    coin(0);
    coin(COIN_SPACING);
    coin(COIN_SPACING * 2);
  } catch (_) {
    // Audio not available — silent fail
  }
};

export const useInvaderGame = () => {
  const [score, setScore] = useState(0);
  const [clicked, setClicked] = useState<Set<string>>(new Set());

  const handleInvaderClick = useCallback((id: string) => {
    setClicked((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      playBlip(next.size);
      setScore((s) => s + 100);
      return next;
    });
  }, []);

  useEffect(() => {
    if (clicked.size === TOTAL_INVADERS) {
      // coins start after kill sound, 3 rings × 0.42s + last ring duration (~0.39s)
      const COINS_DONE_MS = KILL_SOUND_DURATION_MS + (COIN_SPACING * 2 + 0.39) * 1000;

      const coinTimer = setTimeout(playCoin, KILL_SOUND_DURATION_MS);
      const resetTimer = setTimeout(() => {
        setScore(0);
        setClicked(new Set());
      }, COINS_DONE_MS + 400); // small grace after last ring

      return () => {
        clearTimeout(coinTimer);
        clearTimeout(resetTimer);
      };
    }
  }, [clicked]);

  return { score, clicked, handleInvaderClick };
};
